import React, { useCallback, useRef, useState } from 'react';

import styled from 'styled-components';

import { Box, Flex } from '@adsk/alloy-react-box';
import Button from '@adsk/alloy-react-button';
import Theme, { StylableComponent } from '@adsk/alloy-react-theme';

import TrialModal from './components/TrialModal';
import TrialModalOverlay from './components/TrialModalOverlay';
import {
  AUTOSPECS_TRIAL_MARKETING_URL,
  OFFERING_NAMES,
  PRICING_URL,
} from './components/TrialModalItem/helpers/const';
import {
  AUTODESK_PAY_SCRIPT_URLS,
  AUTODESK_PAY_SCRIPT_ELEMENT_ID,
} from './helpers/const';
import {
  BannerMessage,
  Subscription,
  SubscriptionConstruction,
} from './helpers/types';
import { setupAutodeskPaySDK, handleCheckoutFlow } from './helpers/utils';
import { useTranslation } from './i18n';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { getEnv, getCurrentToken } from '@adsk/acs-sc-web-platform-api';
import { useLocale } from '@adsk/alloy-react-locale';
import {
  AlertWarningIcon,
  CheckmarkCircleFilledIcon,
} from '@adsk/alloy-react-icon';
import Tooltip from '@adsk/alloy-react-tooltip';

export const buttonStyle = {
  height: '25px',
  marginLeft: '11px',
  minWidth: '128px',
  ...Theme.typography.ellipsis,
  ...Theme.typography.labelSmall,
};

export const messageStyle = {
  display: 'flex',
  alignItems: 'center',
  ...Theme.typography.bodyMedium,
  ...Theme.typography.ellipsis,
  [Theme.mediaQuery.medium]: {
    display: 'none',
  },
};

type TrialBannerViewProps = StylableComponent<HTMLDivElement> & {
  onClickAnalytics: () => void;
  displayButton: boolean | null;
  message: BannerMessage | null;
  userTrials: Subscription[] | SubscriptionConstruction[];
};

export const MessageDiv = styled.div(messageStyle);

const TrialBannerView = React.forwardRef<HTMLDivElement, TrialBannerViewProps>(
  (
    {
      displayButton = false,
      message = null,
      userTrials,
      onClickAnalytics,
      className,
      style,
      color: _color, // this prop is ignored to fix an error with Box getting wrong color type
      ...props
    }: TrialBannerViewProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const [show, setShow] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [listenerAttached, setListenerAttached] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isCheckoutFlowSuccessful, setIsCheckoutFlowSuccessful] =
      useState(false);
    const [sdkInitialized, setSdkInitialized] = useState(false);
    const popupControlsRef = useRef<{
      focus: () => void;
      close: () => void;
    } | null>(null);
    const { t } = useTranslation();

    const { locale } = useLocale(({ locale }: { locale: string }) =>
      require(`./i18n/${locale}.json`)
    );

    const { value: useCheckoutFlowFlag, loading: loadingCheckoutFlowFlag } =
      useAlloyFeatureFlag('ACSPE_CHECKOUT_FLOW', false);

    const env = getEnv();
    const autodeskPayScriptUrl = AUTODESK_PAY_SCRIPT_URLS[env];

    const warningIconStyle = {
      color: '#FAA21B',
      height: '16px',
      marginLeft: '4px',
      marginTop: '4px',
    };
    const successIconStyle = {
      color: Theme.colors.green500,
      height: '16px',
      marginLeft: '4px',
      marginTop: '4px',
    };

    const justOneTrial = userTrials && userTrials.length === 1;

    const startCheckoutFlow = () => {
      handleCheckoutFlow({
        trial: userTrials[0],
        language: locale ?? 'en',
        onError: (error: string) => {
          setShowOverlay(false);
          setErrorMessage(t(error));
        },
        onComplete: () => {
          setShowOverlay(false);
          setIsCheckoutFlowSuccessful(true);
        },
        onCancel: () => {
          setShowOverlay(false);
        },
        onPopupInit: (controls) => {
          popupControlsRef.current = controls;
        },
      });
    };

    const handleRelaunch = () => {
      popupControlsRef.current?.focus();
    };

    const handleCloseOverlay = () => {
      popupControlsRef.current?.close();
      setShowOverlay(false);
    };

    const handleClickBuyingOptions = () => {
      if (!justOneTrial) {
        setShow(true);
      } else {
        if (useCheckoutFlowFlag && !loadingCheckoutFlowFlag && sdkInitialized) {
          setShowOverlay(true);
          startCheckoutFlow();
        } else {
          const offeringCategory =
            'offeringCategory' in userTrials[0]
              ? userTrials[0].offeringCategory
              : ''; // empty string means the pricing home page
          const nameContainsAutoSpecs = userTrials[0].offeringName.includes(
            OFFERING_NAMES.AUTOSPECS
          );
          const buyingOptionsLink = nameContainsAutoSpecs
            ? AUTOSPECS_TRIAL_MARKETING_URL
            : `${PRICING_URL}/${offeringCategory}`;
          window.open(buyingOptionsLink, '_blank');
        }
      }
      onClickAnalytics?.();
    };

    const onChangeClientState = useCallback(() => {
      if (listenerAttached) return;

      setupAutodeskPaySDK({
        env,
        getAccessToken: async () => {
          const { accessToken } = await getCurrentToken();
          return accessToken;
        },
        scriptElementId: AUTODESK_PAY_SCRIPT_ELEMENT_ID,
        onInitialized: (success: boolean) => {
          setSdkInitialized(success);
        },
      });

      setListenerAttached(true);
    }, [env, listenerAttached]);

    return (
      <HelmetProvider>
        <Helmet onChangeClientState={onChangeClientState}>
          <script
            src={autodeskPayScriptUrl}
            async
            id={AUTODESK_PAY_SCRIPT_ELEMENT_ID}
          ></script>
        </Helmet>
        <Flex
          className={className}
          flexDirection="row"
          style={style}
          ref={ref}
          data-testid="TrialBanner"
          {...props}
        >
          {isCheckoutFlowSuccessful && justOneTrial && (
            <>
              <Tooltip
                content={
                  <Box>
                    <Box>
                      {t('SUBSCRIPTION_PURCHASED_MESSAGE', {
                        subscriptionName: userTrials?.[0]?.offeringName ?? '',
                      })}
                    </Box>
                    <Box>{t('SUBSCRIPTION_ACTIVATION_MESSAGE')}</Box>
                  </Box>
                }
                placement={Tooltip.PLACEMENTS.BOTTOM_START}
                variant={Tooltip.VARIANTS.LIGHT}
                show={true}
                data-testid="TrialBanner_success_message_tooltip"
              >
                <CheckmarkCircleFilledIcon style={successIconStyle} />
              </Tooltip>
            </>
          )}
          <MessageDiv
            style={{
              color:
                message && message.daysLeft <= 3
                  ? Theme.colors.red500
                  : Theme.colors.charcoal900,
            }}
          >
            {message ? message.text : ''}
          </MessageDiv>
          {displayButton && (
            <>
              {errorMessage && (
                <>
                  <Tooltip
                    content={errorMessage}
                    placement={Tooltip.PLACEMENTS.BOTTOM_START}
                    variant={Tooltip.VARIANTS.LIGHT}
                    show={true}
                    data-testid="TrialBanner_error_message_tooltip"
                  >
                    <AlertWarningIcon style={warningIconStyle} />
                  </Tooltip>
                </>
              )}
              <Button
                data-testid="TrialBanner_button"
                style={buttonStyle}
                variant={Button.VARIANTS.PRIMARY}
                onClick={handleClickBuyingOptions}
                disabled={isCheckoutFlowSuccessful && justOneTrial}
              >
                {t('BANNER_BUTTON_LABEL')}
              </Button>
            </>
          )}
          <TrialModal
            data-testid="TrialBanner_modal"
            onClose={() => {
              setShow(false);
            }}
            show={show}
            userTrials={userTrials}
            sdkInitialized={sdkInitialized}
          />
        </Flex>
        <TrialModalOverlay
          show={showOverlay}
          onClose={handleCloseOverlay}
          onRelaunch={handleRelaunch}
        />
      </HelmetProvider>
    );
  }
);

TrialBannerView.displayName = 'TrialBannerView';

export default TrialBannerView;
